import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import {
  Avatar,
  Badge,
  Chip,
  Divider,
  IconButton,
  Menu,
  Typography
} from "@material-ui/core";

import { BiLogOut, BiUser } from "react-icons/bi";
import { IoSettingsOutline } from "react-icons/io5";

import MenuItem from "../MenuItem";
import AccountSelect from "./AccountSelect";
import Can from "modules/user/config/permissions/Can";

import { useUserDropdownStyles } from "./useStyles";
import { usePopupState } from "modules/core/hooks/usePopupState";

import { IUser, UserRole } from "modules/user/types/entities";

const userRoleDisplayText: Record<UserRole, string> = {
  administrator: "administrator",
  program_manager: "program manager",
  executive: "executive",
  master: "master",
  staff: "team user",
  client: "client user",
  team_viewer: "team viewer",
  client_viewer: "client viewer"
};

interface IUserDropdownProps {
  userPhoto?: string;
  userFullname: string;
  userRole: UserRole;
  profileUrl: string;
  accountSettingsUrl: string;
  accounts: IUser["accounts"];
  onLogoutClick: () => void;
}

const UserDropdown: React.FC<IUserDropdownProps> = props => {
  const {
    userPhoto,
    userFullname,
    userRole,
    profileUrl,
    accountSettingsUrl,
    accounts,
    onLogoutClick
  } = props;

  const { anchorEl, handleClose, handleOpen, isOpen } = usePopupState();

  const classes = useUserDropdownStyles({ userRole });

  const userInitials = useMemo(
    () =>
      userFullname
        .split(" ")
        .map(part => part[0])
        .join("")
        .slice(0, 2),
    [userFullname]
  );

  return (
    <>
      <Badge
        overlap="circle"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        variant="dot"
        classes={{
          badge: classes.avatarBadge
        }}
      >
        <IconButton onClick={handleOpen} className={classes.avatar}>
          <Avatar src={userPhoto} variant="circular">
            {userInitials}
          </Avatar>
        </IconButton>
      </Badge>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={isOpen}
        onClose={handleClose}
        getContentAnchorEl={null}
        disableScrollLock
        anchorOrigin={{
          vertical: 50,
          horizontal: -140
        }}
        classes={{ paper: classes.paper, list: classes.menu }}
      >
        {userRole ? (
          <MenuItem
            disabled
            className={classes.head}
            label={
              <Chip
                label={userRoleDisplayText[userRole]}
                color="primary"
                size="small"
                className={classes.chip}
              />
            }
          />
        ) : null}

        <MenuItem
          className={classes.menuItem}
          {...{
            component: Link,
            to: profileUrl
          }}
          label="Profile"
          icon={<BiUser />}
        />

        <Can I="read" a="account">
          <MenuItem
            className={classes.menuItem}
            label="Account Settings"
            icon={<IoSettingsOutline />}
            {...{
              component: Link,
              to: accountSettingsUrl
            }}
          />
        </Can>

        <MenuItem
          className={classes.menuItem}
          button
          onClick={onLogoutClick}
          label="Logout"
          icon={<BiLogOut />}
        />

        {accounts.length > 1 && (
          <div>
            <Divider variant="fullWidth" style={{ margin: "10px 0" }} />

            <Typography variant="subtitle2" gutterBottom>
              Workspaces
            </Typography>

            <AccountSelect accounts={accounts} />
          </div>
        )}
      </Menu>
    </>
  );
};

export default React.memo(UserDropdown);
